import * as React from 'react';
import type { MoveKind } from '@chess/api-model';
import { CHIP_GROUP_LABEL, CHIP_GROUPS, CHIP_VALUES, chipLabel, variationRows, type ChipFilter, type ChipGroup } from './table.ts';

// The chips over the Blunders or Misses table: one run per group (Severity,
// Pattern, Stage, Where you stood, Clock), a chip per value with its count
// of rows under the other groups' chips; a chip with no rows is muted, not
// hidden. The Pattern chips are the motif families; each family chosen
// adds a row of its own under them ("Pin: against the king 4 · against the
// queen 7"), its variations as chips that narrow that family. "Clear" turns
// every chip off. Drawn from props only; the filtering is `table.ts`'s.

export interface MistakeChipsProps {
  kind: MoveKind;
  filter: ChipFilter;
  /** Each chip's count (`chipCounts`); absent while loading. */
  counts?: Record<ChipGroup, Record<string, number>>;
  onToggle?: (group: ChipGroup, value: string) => void;
  onClear?: () => void;
}

export const MistakeChips: React.FC<MistakeChipsProps> = ({ kind, filter, counts, onToggle, onClear }) => {
  const any = CHIP_GROUPS.some((g) => filter[g]?.length);
  const chip = (g: ChipGroup, v: string) => {
    const on = !!filter[g]?.includes(v);
    const count = counts?.[g][v];
    return (
      <button
        key={v}
        type="button"
        className={`mistake-chip${on ? ' mistake-chip-on' : ''}${count === 0 && !on ? ' mistake-chip-empty' : ''}`}
        aria-pressed={on}
        disabled={!onToggle}
        onClick={() => onToggle?.(g, v)}>
        {chipLabel(kind, g, v)}
        {count !== undefined && <span className="mistake-chip-count">{count}</span>}
      </button>
    );
  };
  return (
    <div className="mistake-chips" role="group" aria-label="Filter the table">
      {CHIP_GROUPS.map((g) =>
        g === 'variation' ? (
          // A row per family chosen, with that family's variations.
          variationRows(filter).map((row) => (
            <div key={`variation-${row.family}`} className="mistake-chip-group mistake-chip-variations" role="group" aria-label={`${row.label} variations`}>
              <span className="mistake-chip-group-label">{row.label}:</span>
              {row.values.map((v) => chip(g, v))}
            </div>
          ))
        ) : (
          <div key={g} className="mistake-chip-group" role="group" aria-label={CHIP_GROUP_LABEL[g]}>
            <span className="mistake-chip-group-label">{CHIP_GROUP_LABEL[g]}</span>
            {CHIP_VALUES[g].map((v) => chip(g, v))}
          </div>
        ),
      )}
      {any && (
        <button type="button" className="mistake-chip mistake-chip-clear" onClick={onClear}>
          Clear
        </button>
      )}
    </div>
  );
};
